import React from 'react';
import {useShallow} from 'zustand/react/shallow';
import {designSystemToken, Icon} from '@lightricks/react-design-system';
import type {Avatar, Voice} from '@contentlab/api/avatars';
import ToggleButton from '@contentlab/components/toggle-button';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useAvatarAndVoicesFilters from '@contentlab/hooks/use-avatar-and-voices-filters';
import contentLabStore, {
  AiAvatarEditorTab,
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import {AvatarKind, ConfigMode} from '@contentlab/types/aiAvatars';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import assetUpdaters from '@/api/updaters/content-studio/asset';
import AvatarsTab from './subcomponents/AvatarsTab';
import {
  EditorContentSection,
  EditorHeaderSection,
  EditorMainContainer,
} from './subcomponents/EditorContainer';
import ScriptsTab from './subcomponents/ScriptsTab';
import TabsHeader from './subcomponents/TabsHeader';

interface AiAvatarEditorProps {
  testID?: string;
  defaultTab?: AiAvatarEditorTab;
  onTabChange?: (tab: AiAvatarEditorTab) => void;
  hasUploadedVideo?: boolean;
  onAvatarSelect?: (avatar: Avatar) => void;
  selectedAvatar?: Avatar | null;
  onDraftChanged?: () => void;
  isSaving?: boolean;
  onOpenCustomizeScript?: () => void;
  onWordCountOutOfRange?: (isOutOfRange: boolean) => void;
}

function AiAvatarEditor({
  testID = 'ai-avatar-editor',
  defaultTab = AiAvatarEditorTab.SCRIPTS,
  onTabChange,
  hasUploadedVideo,
  onAvatarSelect,
  selectedAvatar,
  onDraftChanged,
  isSaving,
  onOpenCustomizeScript,
  onWordCountOutOfRange,
}: AiAvatarEditorProps) {
  const {
    avatarSpec,
    selectedAvatarKind,
    activeEditorTab,
    configMode,
    isAvatarPreviewMuted,
    scriptData,
  } = contentLabStore(
    useShallow((state) => ({
      avatarSpec: state.draftAiAvatarProject.avatarSpec,
      selectedAvatarKind: state.draftAiAvatarProject.selectedAvatarKind,
      activeEditorTab: state.draftAiAvatarProject.activeEditorTab,
      configMode: state.draftAiAvatarProject.configMode,
      isAvatarPreviewMuted: state.draftAiAvatarProject.isAvatarPreviewMuted,
      scriptData: state.draftAiAvatarProject.scriptData,
    }))
  );
  const {uiInteraction} = useUiInteractionEvent();

  // Use avatar and voices filters hook
  const {
    filteredAvatars,
    filteredVoices,
    avatarFilters,
    onAvatarFilterChange,
    isLoading,
  } = useAvatarAndVoicesFilters(selectedAvatarKind || AvatarKind.LIBRARY);

  const handleTabChange = (tab: AiAvatarEditorTab, source = 'tabs_click') => {
    if (tab === activeEditorTab) {
      return;
    }
    onTabChange?.(tab);
    contentLabActions.updateDraftAiAvatarProject({activeEditorTab: tab});
    contentLabActions.setDraftIsAvatarLayoutOpen(false);

    uiInteraction({
      item_name: 'avatar_editor_tab',
      interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_EDITOR,
      interaction_details: tab,
      flow_name: source,
    });
  };

  const handleSelectKind = (kind: AvatarKind) => {
    contentLabActions.setDraftAiAvatarProjectSelectedType(kind);
    const spec = contentLabStore.getState().draftAiAvatarProject.avatarSpec;
    const hasLibrary = Boolean(spec.libraryId);
    const hasVoiceOnly = Boolean(spec.voiceIdVoiceOnly || spec.voiceId);
    const hasCustom = Boolean(
      spec.imageAssetId && (spec.voiceIdCustom || spec.voiceId)
    );
    if (kind === AvatarKind.LIBRARY && hasLibrary) onDraftChanged?.();
    if (kind === AvatarKind.VOICE_ONLY && hasVoiceOnly) onDraftChanged?.();
    if (kind === AvatarKind.CUSTOM && hasCustom) onDraftChanged?.();

    uiInteraction({
      item_name: 'avatar_type_selection',
      interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_EDITOR,
      interaction_details: kind,
    });
  };

  const handleOpenConfig = () => {
    const itemName =
      selectedAvatarKind === AvatarKind.CUSTOM
        ? ConfigMode.CUSTOM
        : ConfigMode.LIBRARY;
    contentLabActions.setDraftIsAvatarLayoutOpen(true);
    contentLabActions.setDraftConfigMode(itemName);
    uiInteraction({
      item_name: 'avatar_layout_button',
      interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_EDITOR,
    });
  };

  const handleConfigChange = (partial: Partial<typeof avatarSpec.config>) => {
    if (!configMode) return;
    contentLabActions.updateDraftAiAvatarProjectConfig(partial, configMode);
    onDraftChanged?.();
  };

  const handleCustomSelectVoice = (voice: Voice) => {
    contentLabActions.updateDraftAiAvatarProjectAvatarSpec({
      voiceIdCustom: voice.id,
    });
    contentLabActions.setDraftAiAvatarProjectSelectedType(AvatarKind.CUSTOM);
    const spec = contentLabStore.getState().draftAiAvatarProject.avatarSpec;
    if (spec.imageAssetId) onDraftChanged?.();

    uiInteraction({
      item_name: 'custom_voice_only_voice_selected',
      interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_EDITOR,
      interaction_details: JSON.stringify(voice),
      asset_id: voice.id,
    });
  };

  const handleVoiceOnlySelectVoice = (voice: Voice) => {
    contentLabActions.updateDraftAiAvatarProjectAvatarSpec({
      voiceIdVoiceOnly: voice.id,
    });
    contentLabActions.setDraftAiAvatarProjectSelectedType(
      AvatarKind.VOICE_ONLY
    );
    onDraftChanged?.();
    uiInteraction({
      item_name: 'voice_only_voice_selected',
      interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_EDITOR,
      interaction_details: JSON.stringify(voice),
      asset_id: voice.id,
    });
  };

  const handleCustomSelectImage = async (file: File) => {
    const readImageDimensions = (
      f: File
    ): Promise<{width: number; height: number}> =>
      new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(f);
        img.onload = () => {
          resolve({width: img.width, height: img.height});
          URL.revokeObjectURL(url);
        };
        img.src = url;
      });

    const convertImageToJpeg = (f: File): Promise<File> =>
      new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(f);
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (ctx) ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              URL.revokeObjectURL(url);
              if (!blob) {
                resolve(f);
                return;
              }
              const jpegFile = new File(
                [blob],
                `${f.name.replace(/\.[^.]+$/, '')}.jpg`,
                {type: 'image/jpeg', lastModified: Date.now()}
              );
              resolve(jpegFile);
            },
            'image/jpeg',
            0.92
          );
        };
        img.src = url;
      });

    const uploadFile =
      file.type === 'image/jpeg' ? file : await convertImageToJpeg(file);
    const {width, height} = await readImageDimensions(uploadFile);
    const resp = (await assetUpdaters.getSignedUrls([
      {file: uploadFile, metadata: {height, width, duration_millis: 1}},
    ])) as unknown as {
      urls?: Record<string, {upload_url: string; asset_id: string}>;
    };
    const entry = resp?.urls?.[uploadFile.name];
    if (!entry) return;
    await fetch(entry.upload_url, {
      method: 'PUT',
      headers: {'Content-Type': uploadFile.type},
      body: uploadFile,
    });
    contentLabActions.updateDraftAiAvatarProjectAvatarSpec({
      imageAssetId: entry.asset_id,
    });
    contentLabActions.setDraftAiAvatarProjectSelectedType(AvatarKind.CUSTOM);
    try {
      const previewUrl = URL.createObjectURL(file);
      contentLabActions.updateDraftAiAvatarProjectAvatarSpec({
        imagePreviewUrl: previewUrl,
      });
    } catch {
      // ignore preview failures
    }
    const spec = contentLabStore.getState().draftAiAvatarProject.avatarSpec;
    const hasCustomVoice = !!spec.voiceIdCustom;
    if (hasCustomVoice) onDraftChanged?.();
  };

  const handleAvatarPreviewMuteChange = (avatar: Avatar, muted: boolean) => {
    contentLabActions.updateDraftAiAvatarProject({isAvatarPreviewMuted: muted});
    uiInteraction({
      item_name: 'avatar_preview_mute_toggle',
      interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_EDITOR,
      interaction_details: muted ? 'muted' : 'unmuted',
      asset_id: avatar.id,
    });
  };

  const renderActiveTab = () => {
    switch (activeEditorTab) {
      case AiAvatarEditorTab.SCRIPTS:
        return (
          <ScriptsTab
            onDraftChanged={onDraftChanged}
            onOpenCustomizeScript={onOpenCustomizeScript}
            onWordCountOutOfRange={onWordCountOutOfRange}
            onVersionSelected={(versionIndex: number) => {
              const script = scriptData.scripts[versionIndex];

              if (!script || versionIndex === scriptData.selectedScriptIndex) {
                return;
              }

              uiInteraction({
                item_name: 'script_version_selected',
                interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_EDITOR,
                interaction_details: JSON.stringify({
                  versionNumber: versionIndex + 1,
                  scriptPreview: script.script.slice(0, 30),
                }),
              });
            }}
            onTabChange={handleTabChange}
          />
        );
      case AiAvatarEditorTab.AVATARS:
        return (
          <AvatarsTab
            hasUploadedVideo={hasUploadedVideo}
            onAvatarSelect={(avatar) => {
              onAvatarSelect?.(avatar);
              contentLabActions.updateDraftAiAvatarProjectAvatarSpec({
                libraryId: avatar.id,
              });

              if (!selectedAvatarKind) {
                // select the library kind if none is selected yet
                // the first avatar is automatically selected upon first entry
                handleSelectKind(AvatarKind.LIBRARY);
              }
            }}
            selectedAvatar={selectedAvatar}
            isSaving={isSaving}
            avatarSpec={avatarSpec}
            selectedAvatarKind={selectedAvatarKind}
            onSelectKind={handleSelectKind}
            onOpenConfig={handleOpenConfig}
            voiceOnlySelectedId={
              avatarSpec.voiceIdVoiceOnly || avatarSpec.voiceId || null
            }
            customSelectedVoiceId={
              avatarSpec.voiceIdCustom || avatarSpec.voiceId || null
            }
            onVoiceOnlySelectVoice={handleVoiceOnlySelectVoice}
            onCustomSelectVoice={handleCustomSelectVoice}
            onCustomSelectImage={handleCustomSelectImage}
            isAvatarPreviewMuted={isAvatarPreviewMuted}
            onAvatarPreviewMuteChange={handleAvatarPreviewMuteChange}
            avatarFilters={avatarFilters}
            onAvatarFilterChange={(id: string, selectedOptionId: string[]) => {
              onAvatarFilterChange(id, selectedOptionId);
              uiInteraction({
                item_name: 'avatar_filter_changed',
                interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_EDITOR,
                interaction_details: JSON.stringify({
                  filter_id: id,
                  selected_options: selectedOptionId,
                  avatar_type: selectedAvatarKind,
                }),
                asset_id: id,
              });
            }}
            avatars={filteredAvatars}
            voices={filteredVoices}
            isLoadingAvatarsOrVoices={isLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <EditorMainContainer data-testid={testID}>
      <EditorHeaderSection>
        <TabsHeader titleLocaleKey="Select your script and avatar" />
        <ToggleButton
          testID="avatar-config-tabs-toggle"
          options={[
            {
              label: translate('Script'),
              value: AiAvatarEditorTab.SCRIPTS,
              icon: (
                <Icon
                  name="Navigation-Terms"
                  size="medium"
                  appearance="primary"
                />
              ),
            },
            {
              label: translate('Avatars'),
              value: AiAvatarEditorTab.AVATARS,
              icon: (
                <Icon
                  name="Features-AiAvatars"
                  size="medium"
                  appearance="primary"
                />
              ),
            },
          ]}
          value={activeEditorTab}
          onChange={(tab: string) => handleTabChange(tab as AiAvatarEditorTab)}
          showBorder={false}
          disableSlideAnimation={false}
          layout="equal"
          gap="2px"
          padding="4px"
          borderRadius="16px"
          settings={{
            backgroundColor: designSystemToken('semantic.bg.tertiary'),
            borderRadius: '16px',
            optionColor: 'transparent',
            selectedOptionColor: designSystemToken('semantic.bg.secondary'),
            optionBorderRadius: '12px',
          }}
        />
      </EditorHeaderSection>

      <EditorContentSection>{renderActiveTab()}</EditorContentSection>
    </EditorMainContainer>
  );
}

export default AiAvatarEditor;
